import { io } from 'socket.io-client';
import { getToken } from './cookies';

class ChatSocketService {
  constructor() {
    this.socket = null;
    this.isConnected = false;
    this.currentVenue = null;
    this.currentUser = null;
    this.connectionListeners = [];
    this.messageListeners = [];
    this.typingListeners = [];
    this.userListeners = [];
    this.emojiListeners = [];
    this.vibrationListeners = [];
    
    console.log('🔧 ChatSocketService başlatıldı');
  }

  // Socket bağlantısını başlat
  async connect() {
    if (this.socket?.connected) {
      console.log('✅ Socket zaten bağlı');
      return this.socket;
    }

    const token = await getToken('qriendtoken');
    if (!token) {
      throw new Error('Token bulunamadı. Lütfen giriş yapın.');
    }

    console.log('🔌 Socket bağlantısı başlatılıyor...');
    console.log('🔌 Socket URL:', process.env.REACT_APP_SOCKET_URL || 'https://live.qriend.app');

    this.socket = io(process.env.REACT_APP_SOCKET_URL || 'https://live.qriend.app', {
      auth: {
        token: token
      },
      transports: ['websocket', 'polling'], // Fallback için polling ekle
      withCredentials: true,
      timeout: 20000, // 20 saniye timeout
      forceNew: true, // Yeni bağlantı zorla
      reconnection: true, // Otomatik yeniden bağlanma
      reconnectionAttempts: 5, // 5 kez deneme
      reconnectionDelay: 1000, // 1 saniye bekle
      reconnectionDelayMax: 5000, // Maksimum 5 saniye bekle
      maxReconnectionAttempts: 5, // Maksimum 5 deneme
      autoConnect: true, // Otomatik bağlan
      query: {
        client: 'web' // Web client olduğunu belirt
      }
    });

    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        console.error('❌ Socket bağlantı timeout');
        reject(new Error('Bağlantı zaman aşımına uğradı'));
      }, 25000); // 25 saniye timeout (socket timeout + 5 saniye)

      this.socket.on('connect', () => {
        clearTimeout(timeout);
        this.isConnected = true;
        console.log('✅ Socket bağlantısı başarılı');
        console.log('🔌 Socket ID:', this.socket.id);
        this.setupEventListeners();
        resolve(this.socket);
      });

      this.socket.on('connect_error', (error) => {
        clearTimeout(timeout);
        console.error('❌ Socket bağlantı hatası:', error);
        reject(error);
      });

      this.socket.on('connection_success', (data) => {
        this.currentUser = data.user;
        console.log('🔥 Socket connection_success - Kullanıcı bilgileri alındı:', this.currentUser);
      });
    });
  }

  // Event listener'ları kur
  setupEventListeners() {
    if (!this.socket) {
      console.error('❌ Socket yok, event listener\'lar kurulamadı');
      return;
    }

    console.log('🔧 Event listener\'lar kuruluyor...');

    // Event listener'ları temizle ve yeniden ekle - socketService.js'deki yaklaşım
    this.socket.removeAllListeners('connection_success');
    this.socket.removeAllListeners('user_joined');
    this.socket.removeAllListeners('user_left');
    this.socket.removeAllListeners('new_message');
    this.socket.removeAllListeners('venue_users');
    this.socket.removeAllListeners('venue_messages');
    this.socket.removeAllListeners('typing_status');
    this.socket.removeAllListeners('typing_start');
    this.socket.removeAllListeners('typing_stop');

    // Bağlantı durumu
    this.socket.on('disconnect', (reason) => {
      this.isConnected = false;
      console.log('❌ Socket bağlantısı kesildi, sebep:', reason);
    });

    this.socket.on('reconnect', (attemptNumber) => {
      this.isConnected = true;
      console.log('🔄 Socket yeniden bağlandı, deneme:', attemptNumber);
      // Eğer venue'de ise tekrar join yap
      if (this.currentVenue) {
        console.log('🔄 Venue\'ye tekrar katılıyor:', this.currentVenue);
        this.joinVenue(this.currentVenue);
      }
    });

    // Bağlantı hatası durumunda yeniden deneme
    this.socket.on('connect_error', (error) => {
      // Bağlantı hatası durumunda yeniden deneme
      if (this.socket) {
        setTimeout(() => {
          if (!this.socket.connected) {
            console.log('🔄 Socket yeniden bağlanmaya çalışılıyor...');
            this.socket.connect();
          }
        }, 10000);
      }
    });

    // Mesaj eventi
    this.socket.on('new_message', (data) => {
      console.log('🔴 Socket\'ten gelen mesaj data:', data);
      console.log('🔴 Current user:', this.currentUser);
      
      // Emoji ve vibration reaction'ları normal mesaj olarak işleme
      // Bunlar ayrı event'ler olarak gelmeli
      if (data.type === 'emoji' || data.type === 'vibration') {
        console.log('⚠️ Emoji/Vibration reaction normal mesaj olarak geldi, atlanıyor:', data);
        return;
      }
      
      // Eğer message objesi içinde type varsa da kontrol et
      if (data.message?.type === 'emoji' || data.message?.type === 'vibration') {
        console.log('⚠️ Emoji/Vibration reaction message objesi içinde geldi, atlanıyor:', data);
        return;
      }
      
      this.messageListeners.forEach(listener => {
        try {
          // Daha güvenli ID oluşturma
          let messageId = data.id || data._id;
          if (!messageId) {
            // Fallback: content + timestamp + user id ile unique ID oluştur
            const content = data.message?.content || data.message || '';
            const timestamp = data.timestamp || Date.now();
            const userId = data.user?.id || '';
            messageId = `msg_${timestamp}_${userId}_${content.length}`;
          }

          const messageData = {
            id: messageId,
            text: data.message?.content || data.message,
            type: data.message?.type || 'text',
            senderId: String(data.user.id), // String'e çevir
            senderName: data.user.name || data.user.email,
            senderAvatar: data.user.avatar || null,
            timestamp: data.timestamp || Date.now(),
            status: 'sent',
            backendUserId: String(data.user?.backendUserId || data.user?.userId || data.user?.id || ''), // Backend ID ekle
          };
          
          console.log('🔴 İşlenen mesaj data:', messageData);
          listener(messageData);
        } catch (error) {
          console.error('❌ Mesaj listener hatası:', error);
        }
      });
    });

    // Kullanıcı eventi
    this.socket.on('venue_users', (users) => {
      console.log('👥 venue_users eventi:', users);
      this.userListeners.forEach(listener => {
        try {
          listener(users);
        } catch (error) {
          console.error('❌ User listener hatası:', error);
        }
      });
    });

    // Typing eventi
    this.socket.on('user_typing', (data) => {
      console.log('⌨️ user_typing eventi:', data);
      this.typingListeners.forEach(listener => {
        try {
          listener(data);
        } catch (error) {
          console.error('❌ Typing listener hatası:', error);
        }
      });
    });

    // Emoji reaction eventi
    this.socket.on('emoji_reaction', (data) => {
      console.log('🎉 Socket\'ten gelen emoji reaction:', data);
      console.log('🎉 Emoji reaction işleniyor...');
      
      this.emojiListeners.forEach(listener => {
        try {
          const reactionData = {
            id: data.id || Date.now().toString(),
            emoji: data.emoji,
            senderId: String(data.user.id), // String'e çevir
            senderName: data.user.name || data.user.email,
            senderAvatar: data.user.avatar,
            timestamp: data.timestamp || Date.now()
          };
          console.log('🎉 İşlenen emoji reaction data:', reactionData);
          listener(reactionData);
        } catch (error) {
          console.error('❌ Emoji listener hatası:', error);
        }
      });
    });

    // Vibration reaction eventi
    this.socket.on('vibration_reaction', (data) => {
      console.log('📳 Socket\'ten gelen vibration reaction:', data);
      console.log('📳 Vibration reaction işleniyor...');
      
      this.vibrationListeners.forEach(listener => {
        try {
          const vibrationData = {
            id: data.id || Date.now().toString(),
            senderId: String(data.user.id), // String'e çevir
            senderName: data.user.name || data.user.email,
            senderAvatar: data.user.avatar,
            timestamp: data.timestamp || Date.now()
          };
          console.log('📳 İşlenen vibration reaction data:', vibrationData);
          listener(vibrationData);
        } catch (error) {
          console.error('❌ Vibration listener hatası:', error);
        }
      });
    });

    console.log('✅ Event listener\'lar kuruldu');
  }

  // Venue'ye katıl
  joinVenue(venueId) {
    if (!this.socket?.connected) {
      throw new Error('Socket bağlantısı yok');
    }

    if (this.currentVenue && this.currentVenue !== venueId) {
      this.socket.leave(this.currentVenue);
    }

    this.currentVenue = venueId;
    console.log('📍 Venue\'ye katılıyor:', venueId);
    
    // Venue'ye katıl
    this.socket.emit('join_venue', venueId);
    
    return new Promise((resolve, reject) => {
      // Önceki listener'ları temizle
      this.socket.off('venue_messages');

      // Eski mesajları al - tek seferlik listener
      const handleVenueMessages = (messages) => {
        console.log('📥 Backend\'den gelen eski mesajlar:', messages);
        console.log('📊 Mesaj sayısı:', messages?.length || 0);
        
        this.socket.off('venue_messages', handleVenueMessages);
        
        if (!messages || !Array.isArray(messages)) {
          console.warn('⚠️ Geçersiz mesaj formatı:', messages);
          resolve([]);
          return;
        }

        const formattedMessages = messages.map((msg, index) => {
          console.log(`📋 Mesaj ${index + 1}:`, msg);
          console.log(`👤 User objesi:`, msg.user);
          
          // Daha güvenli ID oluşturma
          let messageId = msg.id || msg._id;
          if (!messageId) {
            const content = msg.message?.content || msg.message || '';
            const timestamp = msg.timestamp || Date.now();
            const userId = msg.user?.id || '';
            messageId = `msg_${timestamp}_${userId}_${content.length}`;
          }

          // Backend user ID'yi daha akıllıca al
          let backendUserId = msg.user?.backendUserId || msg.user?.userId || msg.user?.realId;
          
          // Eğer backend ID yoksa ve user.id numeric ise, o backend ID olabilir
          if (!backendUserId && msg.user?.id && !isNaN(msg.user.id)) {
            backendUserId = String(msg.user.id);
          }
          
          // Hala yoksa socket ID'yi kullan (fallback)
          if (!backendUserId) {
            backendUserId = String(msg.user?.id || '');
          }

          console.log(`🆔 Backend User ID: ${backendUserId} (from: ${msg.user?.id})`);

          return {
            id: messageId,
            text: msg.message?.content || msg.message,
            type: msg.message?.type || 'text',
            senderId: String(msg.user?.id || ''), // String'e çevir
            senderName: msg.user?.name || msg.user?.email || 'Bilinmeyen',
            senderAvatar: msg.user?.avatar || null,
            timestamp: msg.timestamp || Date.now(),
            status: 'sent',
            backendUserId: backendUserId, // Daha akıllı backend ID
          };
        });
        
        console.log('✅ Formatlanmış mesajlar:', formattedMessages);
        resolve(formattedMessages);
      };

      // venue_messages event'ini dinle
      this.socket.on('venue_messages', handleVenueMessages);
      
      // Önceki mesajları almak için request gönder - socketService.js formatı ile uyumlu
      const requestData = { venueId: venueId };
      console.log('📤 venue_messages_request gönderiliyor:', requestData);
      this.socket.emit('venue_messages_request', requestData);
      
      // 3 saniye timeout ile fallback - socketService.js'deki yaklaşım
      setTimeout(() => {
        console.log('⏰ 3 saniye timeout - venue_messages eventi gelmedi, boş array döndürülüyor');
        this.socket.off('venue_messages', handleVenueMessages);
        resolve([]); // Boş array gönder ki loading durumu
      }, 3000);
    });
  }

  // Venue'den ayrıl
  leaveVenue() {
    if (this.currentVenue) {
      console.log(`🚪 Venue'den ayrılıyor: ${this.currentVenue}`);
      
      // Socket.io client API'sinde leave metodu yok
      // Room'dan ayrılma server tarafında disconnect sırasında otomatik olarak yapılır
      // Ancak server'a bilgi verebiliriz
      if (this.socket?.connected) {
        const leaveData = { venueId: this.currentVenue };
        console.log('📤 leave_venue eventi gönderiliyor:', leaveData);
        this.socket.emit('leave_venue', leaveData);
        console.log(`📤 leave_venue eventi gönderildi: ${this.currentVenue}`);
      }
      
      // Sadece client state'ini temizle
      this.currentVenue = null;
      console.log('✅ Venue state temizlendi');
    }
  }

  // Mesaj gönder
  sendMessage(venueId, message, type = 'text') {
    if (!this.socket?.connected) {
      throw new Error('Socket bağlantısı yok');
    }

    console.log('🔥 Mesaj gönderiliyor:', { venueId, message, type });

    return new Promise((resolve, reject) => {
      // socketService.js'deki format ile uyumlu
      const messageData = {
        venueId: venueId,
        message: message,  // Direkt message string (nested değil)
        type: type
      };

      console.log('📤 Gönderilen messageData:', messageData);

      this.socket.emit('send_message', messageData, (response) => {
        if (response?.success) {
          console.log('✅ Mesaj başarıyla gönderildi:', response);
          resolve(response);
        } else {
          console.error('❌ Mesaj gönderilemedi:', response);
          reject(new Error(response?.error || 'Mesaj gönderilemedi'));
        }
      });
    });
  }

  // Typing durumu gönder
  sendTypingStatus(venueId, isTyping) {
    if (!this.socket?.connected) return;

    console.log('⌨️ Typing durumu gönderiliyor:', { venueId, isTyping });

    // socketService.js'deki format ile uyumlu
    const typingData = {
      venueId: venueId,
      isTyping: isTyping
    };

    console.log('📤 Gönderilen typing data:', typingData);

    this.socket.emit('typing_status', typingData);
  }

  // Emoji reaction gönder
  sendEmojiReaction(venueId, emoji) {
    if (!this.socket?.connected) {
      throw new Error('Socket bağlantısı yok');
    }

    console.log('🎉 Emoji reaction gönderiliyor:', { venueId, emoji });

    return new Promise((resolve, reject) => {
      // Emoji reaction'ı özel event olarak gönder
      // Normal mesaj olarak değil, emoji_reaction eventi olarak
      const reactionData = {
        venueId: venueId,
        emoji: emoji
      };

      console.log('📤 Gönderilen emoji reaction data:', reactionData);

      // Emoji reaction'ı özel event olarak gönder
      this.socket.emit('emoji_reaction', reactionData, (response) => {
        if (response?.success) {
          console.log('✅ Emoji reaction başarıyla gönderildi:', response);
          resolve(response);
        } else {
          console.error('❌ Emoji reaction gönderilemedi:', response);
          reject(new Error(response?.error || 'Emoji gönderilemedi'));
        }
      });
    });
  }

  // Vibration reaction gönder
  sendVibrationReaction(venueId) {
    if (!this.socket?.connected) {
      throw new Error('Socket bağlantısı yok');
    }

    console.log('📳 Vibration reaction gönderiliyor:', { venueId });

    return new Promise((resolve, reject) => {
      // Vibration reaction'ı özel event olarak gönder
      // Normal mesaj olarak değil, vibration_reaction eventi olarak
      const reactionData = {
        venueId: venueId
      };

      console.log('📤 Gönderilen vibration reaction data:', reactionData);

      // Vibration reaction'ı özel event olarak gönder
      this.socket.emit('vibration_reaction', reactionData, (response) => {
        if (response?.success) {
          console.log('✅ Vibration reaction başarıyla gönderildi:', response);
          resolve(response);
        } else {
          console.error('❌ Vibration reaction gönderilemedi:', response);
          reject(new Error(response?.error || 'Titreşim gönderilemedi'));
        }
      });
    });
  }

  // Listener ekle
  addMessageListener(listener) {
    this.messageListeners.push(listener);
    return () => {
      this.messageListeners = this.messageListeners.filter(l => l !== listener);
    };
  }

  addTypingListener(listener) {
    this.typingListeners.push(listener);
    return () => {
      this.typingListeners = this.typingListeners.filter(l => l !== listener);
    };
  }

  addUserListener(listener) {
    this.userListeners.push(listener);
    return () => {
      this.userListeners = this.userListeners.filter(l => l !== listener);
    };
  }

  addEmojiListener(listener) {
    this.emojiListeners.push(listener);
    return () => {
      this.emojiListeners = this.emojiListeners.filter(l => l !== listener);
    };
  }

  addVibrationListener(listener) {
    this.vibrationListeners.push(listener);
    return () => {
      this.vibrationListeners = this.vibrationListeners.filter(l => l !== listener);
    };
  }

  // Bağlantıyı kapat
  disconnect() {
    if (this.socket) {
      // Eğer venue'de ise önce ayrıl
      if (this.currentVenue) {
        this.leaveVenue();
      }
      
      // Event listener'ları temizle
      this.socket.removeAllListeners();
      
      this.socket.disconnect();
      this.socket = null;
      this.isConnected = false;
      this.currentVenue = null;
      this.currentUser = null;
      
      // Listener'ları temizle
      this.messageListeners = [];
      this.typingListeners = [];
      this.userListeners = [];
      this.emojiListeners = [];
      this.vibrationListeners = [];
    }
  }

  // Getter'lar
  get connected() {
    return this.isConnected && this.socket?.connected;
  }

  get user() {
    return this.currentUser;
  }

  get venue() {
    return this.currentVenue;
  }
}

// Singleton instance
const chatSocketService = new ChatSocketService();

console.log('🚀 ChatSocketService singleton instance oluşturuldu');

export default chatSocketService;
